import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Modal,
  NumberInput,
  TextAreaInput,
  useToast,
} from 'corso-admin-ui';
import { crewMerchantShippingClaimApi } from 'corso-types';
import { useForm } from 'react-hook-form';
import { useCurrency } from '~/hooks/useCurrency';
import { isErrorMessage } from '~/utils/errorResponse';
import { useShippingClaimManualRefund } from '../hooks/useShippingClaimManualRefund';

const manualRefundSchema =
  crewMerchantShippingClaimApi[
    '/:storeId/shipping-protection/claim/:shippingClaimId/manual-refund'
  ].post.request.body;

export function ManualRefundShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { currencyCode, currencySymbol } = useCurrency();
  const { mutate: manualRefund, isPending } = useShippingClaimManualRefund();

  const formId = 'manual-refund-shipping-claim-form';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(manualRefundSchema),
    defaultValues: { amount: 0, comment: '' },
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Manual Refund"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Record Refund
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((data) =>
            manualRefund(data, {
              onSuccess: closeAndReset,
              onError: (error) =>
                toast.show(
                  isErrorMessage(error)
                    ? error.message
                    : 'Could not record this refund. Please try again.',
                ),
            }),
          )(e).catch((error: unknown) =>
            console.error('Failed to record a manual refund', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <Alert
          variant="tip"
          heading="This records a refund issued outside of Corso and resolves the claim as refunded."
        />
        <NumberInput
          id="manual-refund-shipping-claim-amount"
          label="Refund Amount"
          details="The amount refunded to the customer."
          required
          step="0.01"
          addon={{
            insideStart: currencySymbol,
            insideEnd: currencyCode,
          }}
          {...register('amount', { valueAsNumber: true })}
          error={errors.amount?.message}
        />
        <TextAreaInput
          id="manual-refund-shipping-claim-comment"
          label="Comment"
          details="This comment will be added to the timeline."
          rows={3}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </form>
    </Modal>
  );
}
